import React, { useCallback, useState } from 'react';
import { View, StyleSheet, TextInput, Pressable, ActivityIndicator } from 'react-native';
import {
  Bevel,
  Text,
  colors,
  fontSizes,
  fonts,
  makeStyles,
  useTheme,
} from '@ogaim/ui';
import {
  buildImageTag,
  buildRichText,
  canPickImages,
  pickImage,
  uploadImage,
} from '@ogaim/core';

/** The eight colours AIM's picker offered. */
const PALETTE = [
  '#000000', '#ff0000', '#008000', '#0000ff',
  '#800080', '#ff8000', '#008080', '#808080',
];

/** HTML font sizes AIM exposed, with the labels it used. */
const SIZES: Array<{ label: string; size: number }> = [
  { label: 'S', size: 2 },
  { label: 'M', size: 3 },
  { label: 'L', size: 5 },
];

export interface RichTextValue {
  /** Plain text the user typed. */
  text: string;
  /** Uploaded image URLs, appended after the text on submit. */
  images: string[];
}

export interface RichTextEditorProps {
  value: RichTextValue;
  onChange: (next: RichTextValue) => void;
  onSubmit?: () => void;
  placeholder?: string;
  editable?: boolean;
  /** Height of the typing area. */
  height?: number;
  /** Enter submits rather than inserting a newline. */
  submitOnEnter?: boolean;
  autoFocus?: boolean;
  accessibilityLabel?: string;
}

/**
 * The formatting bar and typing area shared by the IM composer and the away
 * message editor.
 *
 * Styling applies to the whole body rather than to a selection. That is a
 * deliberate match for AIM, whose toolbar set the style of the *message*, not
 * of selected runs — and it keeps the value a plain string, so a draft can be
 * stored and restored without carrying a document model around.
 */
export function RichTextEditor({
  value,
  onChange,
  onSubmit,
  placeholder,
  editable = true,
  height = 66,
  submitOnEnter = false,
  autoFocus = false,
  accessibilityLabel,
}: RichTextEditorProps) {
  const theme = useTheme();
  const styles = useStyles();
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const [underline, setUnderline] = useState(false);
  const [color, setColor] = useState<string | undefined>();
  const [size, setSize] = useState<number | undefined>();
  const [showColors, setShowColors] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const attach = useCallback(async () => {
    setError(null);
    try {
      const picked = await pickImage();
      if (!picked) return;
      setUploading(true);
      const url = await uploadImage(picked);
      onChange({ ...value, images: [...value.images, url] });
    } catch (err: any) {
      setError(err?.message ?? 'The image could not be attached.');
    } finally {
      setUploading(false);
    }
  }, [onChange, value]);

  const removeImage = (url: string) =>
    onChange({ ...value, images: value.images.filter((u) => u !== url) });

  return (
    <View style={styles.root}>
      <View style={styles.toolbar}>
        <Toggle label="B" active={bold} onPress={() => setBold((v) => !v)} bold disabled={!editable} />
        <Toggle label="I" active={italic} onPress={() => setItalic((v) => !v)} italic disabled={!editable} />
        <Toggle label="U" active={underline} onPress={() => setUnderline((v) => !v)} underline disabled={!editable} />

        <View style={styles.sep} />

        {SIZES.map((s) => (
          <Toggle
            key={s.label}
            label={s.label}
            active={size === s.size}
            onPress={() => setSize(size === s.size ? undefined : s.size)}
            disabled={!editable}
          />
        ))}

        <View style={styles.sep} />

        <Toggle
          label="A"
          active={showColors}
          onPress={() => setShowColors((v) => !v)}
          disabled={!editable}
          swatch={color}
          accessibilityLabel="Text colour"
        />

        {canPickImages() ? (
          <Toggle
            label="🖼"
            active={false}
            onPress={attach}
            disabled={!editable || uploading}
            accessibilityLabel="Attach an image"
          />
        ) : null}

        {uploading ? <ActivityIndicator size="small" style={styles.spinner} /> : null}
      </View>

      {showColors ? (
        <Bevel variant="raised" style={styles.palette} contentStyle={styles.paletteInner}>
          {PALETTE.map((c) => (
            <Pressable
              key={c}
              onPress={() => {
                setColor(c === color ? undefined : c);
                setShowColors(false);
              }}
              accessibilityRole="button"
              accessibilityLabel={`Colour ${c}`}
              style={styles.swatchHit}
            >
              <Bevel variant={c === color ? 'pressed' : 'raised'} background={c} style={styles.swatch} />
            </Pressable>
          ))}
        </Bevel>
      ) : null}

      <Bevel
        variant="sunken"
        background={editable ? theme.colors.fieldBg : theme.colors.face}
        style={[styles.field, { height }]}
        contentStyle={styles.fieldInner}
      >
        <TextInput
          value={value.text}
          onChangeText={(text) => onChange({ ...value, text })}
          onSubmitEditing={submitOnEnter ? onSubmit : undefined}
          editable={editable}
          multiline
          blurOnSubmit={false}
          autoFocus={autoFocus}
          returnKeyType={submitOnEnter ? 'send' : 'default'}
          autoCorrect={false}
          spellCheck={false}
          placeholder={placeholder}
          placeholderTextColor={theme.colors.disabledText}
          accessibilityLabel={accessibilityLabel}
          style={[
            styles.input,
            bold && styles.bold,
            italic && styles.italic,
            underline && styles.underline,
            color ? { color } : null,
          ]}
        />
      </Bevel>

      {value.images.length > 0 ? (
        <View style={styles.attachments}>
          {value.images.map((url) => (
            <Pressable
              key={url}
              onPress={() => removeImage(url)}
              accessibilityRole="button"
              accessibilityLabel="Remove this image"
            >
              <Bevel variant="raised" style={styles.chip} contentStyle={styles.chipInner}>
                <Text numberOfLines={1} style={styles.chipText}>
                  🖼 image ✕
                </Text>
              </Bevel>
            </Pressable>
          ))}
        </View>
      ) : null}

      {error ? (
        <Text style={styles.error} numberOfLines={2}>
          {error}
        </Text>
      ) : null}
    </View>
  );
}

/**
 * Turns editor state into a body. Images follow the text on their own lines,
 * which is how AIM rendered an inline image.
 */
export function toBody(
  value: RichTextValue,
  style: { bold?: boolean; italic?: boolean; underline?: boolean; color?: string; size?: number } = {}
): string {
  const parts: string[] = [];
  const text = value.text.trim();
  if (text) parts.push(buildRichText(text, style));
  for (const url of value.images) parts.push(buildImageTag(url));
  return parts.join('<br>');
}

export const emptyRichText: RichTextValue = { text: '', images: [] };

function Toggle({
  label,
  active,
  onPress,
  bold,
  italic,
  underline,
  disabled,
  swatch,
  accessibilityLabel,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  disabled?: boolean;
  swatch?: string;
  accessibilityLabel?: string;
}) {
  const styles = useStyles();
  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ selected: active, disabled: !!disabled }}
      accessibilityLabel={accessibilityLabel ?? label}
      hitSlop={4}
    >
      <Bevel
        variant={active ? 'pressed' : 'raised'}
        style={styles.toggle}
        contentStyle={styles.toggleInner}
      >
        <Text
          disabled={disabled}
          style={[bold && styles.bold, italic && styles.italic, underline && styles.underline]}
        >
          {label}
        </Text>
        {swatch ? <View style={[styles.swatchBar, { backgroundColor: swatch }]} /> : null}
      </Bevel>
    </Pressable>
  );
}

const useStyles = makeStyles((t) => ({
  root: { gap: 3 },
  toolbar: { flexDirection: 'row', alignItems: 'center', gap: 2 },
  toggle: { width: 22, height: 20 },
  toggleInner: { alignItems: 'center', justifyContent: 'center' },
  sep: { width: 1, height: 16, backgroundColor: t.colors.shadow, marginHorizontal: 3 },
  spinner: { marginLeft: 6 },

  palette: { alignSelf: 'flex-start' },
  paletteInner: { flexDirection: 'row', padding: 3, gap: 3 },
  swatchHit: { padding: 1 },
  swatch: { width: 16, height: 16 },
  swatchBar: { position: 'absolute', bottom: 1, left: 3, right: 3, height: 2 },

  field: {},
  fieldInner: { padding: 3 },
  input: {
    flex: 1,
    fontFamily: t.fonts.message,
    fontSize: t.fontSizes.message,
    color: t.colors.windowText,
    padding: 0,
    margin: 0,
    textAlignVertical: 'top',
  },
  bold: { fontWeight: 'bold' },
  italic: { fontStyle: 'italic' },
  underline: { textDecorationLine: 'underline' },

  attachments: { flexDirection: 'row', flexWrap: 'wrap', gap: 3 },
  chip: { maxWidth: 130 },
  chipInner: { paddingHorizontal: 5, paddingVertical: 2 },
  chipText: { fontSize: 10 },

  error: { color: '#800000', fontSize: 10 },
}));
